import { computeIntake } from './intake.js';
import { addCalendarDays, isoDate, median, roundHalfUp } from './math.js';
import { summarizeAdherence } from './adherence.js';
import { expandSlots } from './schedule.js';
import { treatmentPlanAt } from './treatment-lifecycle.js';
export function seriesStats(values) {
    const clean = values.filter((value) => Number.isFinite(value));
    if (!clean.length)
        return { count: 0, median: null, min: null, max: null };
    return { count: clean.length, median: roundHalfUp(median([...clean]), 1), min: Math.min(...clean), max: Math.max(...clean) };
}
const live = (rows) => rows.filter((row) => !row.deletedAt && !row.duplicateOf);
const dayOf = (epoch) => isoDate(epoch, 'Asia/Seoul');
const startOf = (date) => Date.parse(`${date}T00:00:00+09:00`);
const endOf = (date) => Date.parse(`${date}T23:59:59.999+09:00`);
/** R2·R3 — 기간 요약. 판정어 없이 숫자만. 리포트 PDF와 같은 원본·같은 함수로 계산한다. */
export function summarizePeriod(input) {
    const fromEpoch = startOf(input.from);
    const toEpoch = Math.min(endOf(input.to), input.now ?? Number.POSITIVE_INFINITY);
    const inRange = (epoch) => epoch >= fromEpoch && epoch <= toEpoch;
    const mine = (rows) => live(rows).filter((row) => row.petId === input.petId);
    let totalDays = 0;
    for (let day = input.from; day <= input.to; day = addCalendarDays(day, 1))
        totalDays += 1;
    const quiet = new Set(mine(input.dayMarks).filter((mark) => mark.kind === 'quiet' && mark.date >= input.from && mark.date <= input.to).map((mark) => mark.date));
    const recorded = new Set(input.recordTimes.filter(inRange).map(dayOf));
    // 별일 없음 확인과 실제 기록은 같은 날 함께 존재할 수 있다. 어느 쪽도 다른 사실을 지우지 않는다.
    const observedDays = new Set([...recorded, ...quiet]).size;
    const srr = mine(input.vitals).filter((row) => row.type === 'sleepingRespiratoryRate' && inRange(row.measuredAt)).map((row) => row.value);
    const weights = mine(input.vitals).filter((row) => row.type === 'weight' && inRange(row.measuredAt)).sort((a, b) => a.measuredAt - b.measuredAt);
    let expected = 0;
    let recordedDoses = 0;
    let skipped = 0;
    let missing = 0;
    for (const treatment of mine(input.treatments)) {
        const slots = expandSlots(treatment, 'Asia/Seoul', input.from, input.to).filter((slot) => slot.epoch <= toEpoch);
        const summary = summarizeAdherence(slots, mine(input.treatmentLogs).filter((log) => log.treatmentId === treatment.id));
        expected += summary.expected;
        recordedDoses += summary.expected - summary.missing;
        skipped += summary.skipped;
        missing += summary.missing;
    }
    return {
        from: input.from, to: input.to, totalDays,
        recordedDays: recorded.size, quietDays: quiet.size, observedDays, recordedDates: [...recorded].sort(),
        respiratory: seriesStats(srr),
        weight: { first: weights[0]?.value ?? null, last: weights.at(-1)?.value ?? null, count: weights.length },
        doses: { expected, recorded: recordedDoses, skipped, missing },
        eventCount: mine(input.events).filter((row) => inRange(row.occurredAt)).length,
    };
}
/** R3 — 최근 n개월 월별 표. 측정 없는 달은 count 0(0으로 그리지 않음). */
export function monthlyVitalTable(vitals, petId, type, months, today) {
    const rows = live(vitals).filter((row) => row.petId === petId && row.type === type);
    const [year, month] = [Number(today.slice(0, 4)), Number(today.slice(5, 7))];
    return Array.from({ length: months }, (_, index) => {
        const date = new Date(Date.UTC(year, month - 1 - index, 1));
        const key = date.toISOString().slice(0, 7);
        const matching = rows.filter((row) => dayOf(row.measuredAt).startsWith(key));
        return { month: key, days: new Set(matching.map((row) => dayOf(row.measuredAt))).size, ...seriesStats(matching.map((row) => row.value)) };
    });
}
/** R4 — 기침 낮·밤 주간 합계. 관찰하지 않은 주는 null(0과 구분). */
export function weeklyCough(logs, petId, weeks, today) {
    const rows = live(logs).filter((row) => row.petId === petId);
    const weekday = new Date(`${today}T00:00:00Z`).getUTCDay();
    const monday = addCalendarDays(today, -((weekday + 6) % 7));
    return Array.from({ length: weeks }, (_, index) => {
        const weekStart = addCalendarDays(monday, -7 * (weeks - 1 - index));
        const weekEnd = addCalendarDays(weekStart, 6);
        const inWeek = rows.filter((row) => row.date >= weekStart && row.date <= weekEnd);
        const observed = (kind) => inWeek.filter((row) => row[`${kind}Observed`] ?? row[`${kind}Count`] > 0);
        const dayRows = observed('day');
        const nightRows = observed('night');
        return { weekStart, day: dayRows.length ? dayRows.reduce((sum, row) => sum + row.dayCount, 0) : null, night: nightRows.length ? nightRows.reduce((sum, row) => sum + row.nightCount, 0) : null };
    });
}
/** R6 — 바뀐 점만 모은 연표. 매일 사실 피드(buildCareTimeline)가 아니라 약 계획 버전·사료 이름·방문 유형·재검을 읽는다. */
export function buildChangeTimeline(input) {
    const out = [];
    for (const treatment of live(input.treatments).filter((row) => row.petId === input.petId && !row.isDraft)) {
        const versions = (treatment.planVersions?.length ? treatment.planVersions : [treatmentPlanAt(treatment, Number.MIN_SAFE_INTEGER)]).filter((version) => !version.cancelledAt).sort((a, b) => a.effectiveAt - b.effectiveAt);
        versions.forEach((version, index) => {
            const at = Number.isFinite(version.effectiveAt) && version.effectiveAt > 0 ? version.effectiveAt : startOf(version.startDate ?? treatment.startDate ?? dayOf(treatment.createdAt));
            const previous = versions[index - 1];
            let kind = null;
            let text = '';
            if (!previous) {
                if (version.state === 'active') {
                    kind = 'medStart';
                    text = `${version.name} 시작${version.doseText ? ` · ${version.doseText}` : ''}`;
                }
            }
            else if (version.state === 'ended' && previous.state !== 'ended') {
                kind = 'medStop';
                text = `${version.name} 중단`;
            }
            else if (version.state === 'paused' && previous.state === 'active') {
                kind = 'medPause';
                text = `${version.name} 잠시 중단`;
            }
            else if (version.state === 'active' && previous.state !== 'active') {
                kind = 'medStart';
                text = `${version.name} ${versions.slice(0, index).some(item => item.state === 'active') ? '다시 시작' : '시작'}`;
            }
            else if ((version.doseText ?? '') !== (previous.doseText ?? '')) {
                kind = 'medDose';
                text = `${version.name} 용량 변경 · ${previous.doseText || '미입력'} → ${version.doseText || '미입력'}`;
            }
            if (kind && Number.isFinite(at))
                out.push({ date: dayOf(at), at, kind, text });
        });
    }
    const meals = live(input.meals).filter((row) => row.petId === input.petId && row.foodName.trim()).sort((a, b) => a.servedAt - b.servedAt);
    let lastFood = null;
    for (const meal of meals) {
        const name = meal.foodName.trim();
        if (meal.foodType === 'treat' || meal.foodType === 'supplement')
            continue;
        if (lastFood && name !== lastFood)
            out.push({ date: dayOf(meal.servedAt), at: meal.servedAt, kind: 'food', text: `사료 바뀜 · ${lastFood} → ${name}` });
        lastFood = name;
    }
    for (const visit of live(input.visits ?? []).filter((row) => row.petId === input.petId)) {
        if (visit.surgery)
            out.push({ date: dayOf(visit.visitedAt), at: visit.visitedAt, kind: 'surgery', text: `수술 · ${visit.surgery.name}${visit.clinicName ? ` · ${visit.clinicName}` : ''}` });
        else if (visit.visitType && visit.visitType !== 'other' && visit.visitType !== 'vaccination')
            out.push({ date: dayOf(visit.visitedAt), at: visit.visitedAt, kind: 'visit', text: `${{ recheck: '재진', sick: '아파서 방문', checkup: '건강검진', emergency: '응급 방문', dental: '치과', surgery: '수술' }[visit.visitType]}${visit.clinicName ? ` · ${visit.clinicName}` : ''}${visit.diagnosisText ? ` · ${visit.diagnosisText}` : ''}` });
    }
    for (const routine of live(input.routines ?? []).filter((row) => row.petId === input.petId && row.kind === 'recheck'))
        out.push({ date: routine.startDate, at: startOf(routine.startDate), kind: 'recheck', text: `재검 예정 · ${routine.name}` });
    return out.sort((a, b) => b.at - a.at);
}
/** R5 바뀐 날 후보: 약 계획 변경일 + 사료 이름이 바뀐 날. */
export function changeDateCandidates(lines) {
    return lines.filter((line) => line.kind !== 'recheck' && line.kind !== 'visit').map((line) => ({ date: line.date, label: line.text }));
}
/**
 * R5 — 바뀐 날 앞뒤 n일. 바뀐 날 당일은 뒤 구간에 넣는다. 측정이 2개 미만인 칸은 화면에서 "측정 부족(n개)"로 보여주고
 * 숫자 비교를 하지 않는다(이 함수는 개수를 그대로 돌려준다). 해석 문장은 만들지 않는다.
 */
export function compareAround(input) {
    const side = (from, to) => {
        const lo = startOf(from);
        const hi = endOf(to);
        const vitals = live(input.vitals).filter((row) => row.petId === input.petId && row.measuredAt >= lo && row.measuredAt <= hi);
        const cough = live(input.coughLogs).filter((row) => row.petId === input.petId && row.date >= from && row.date <= to && ((row.dayObserved ?? row.dayCount > 0) || (row.nightObserved ?? row.nightCount > 0)));
        const eaten = live(input.meals).filter((row) => row.petId === input.petId && row.servedAt >= lo && row.servedAt <= hi).map((row) => computeIntake(row)).flatMap((row) => row.ok && row.eatenG != null ? [row.eatenG] : []);
        return { respiratory: seriesStats(vitals.filter((row) => row.type === 'sleepingRespiratoryRate').map((row) => row.value)), weight: seriesStats(vitals.filter((row) => row.type === 'weight').map((row) => row.value)), cough: seriesStats(cough.map((row) => row.dayCount + row.nightCount)), eatenG: seriesStats(eaten) };
    };
    return { date: input.date, days: input.days, before: side(addCalendarDays(input.date, -input.days), addCalendarDays(input.date, -1)), after: side(input.date, addCalendarDays(input.date, input.days - 1)) };
}
/** 월요일 기준 이번 주 시작일. */
export function weekStartOf(date) {
    const weekday = new Date(`${date}T00:00:00Z`).getUTCDay();
    return addCalendarDays(date, -((weekday + 6) % 7));
}
